import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HardDrive, Users, Camera, TrendingUp, CameraOff, ChevronDown, Baby, GraduationCap } from 'lucide-react';
import { useStorageUsage, useActiveUsers, useParentsWithoutPhotoConsent, useTeacherChildRatio } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';

// Storage limit in GB (configurable per plan)
const STORAGE_LIMIT_GB = 10;

export default function OtherMetricsSection({ branchId }) {
  const [noConsentExpanded, setNoConsentExpanded] = useState(false);

  const {
    data: storageData,
    isLoading: storageLoading
  } = useStorageUsage(branchId);

  const {
    data: activeUsersData,
    isLoading: activeUsersLoading
  } = useActiveUsers(branchId);

  const {
    data: photoConsentData,
    isLoading: photoConsentLoading
  } = useParentsWithoutPhotoConsent(branchId);

  const {
    data: ratioData,
    isLoading: ratioLoading
  } = useTeacherChildRatio(branchId);

  // Calculate storage percentage
  const storagePercent = storageData
    ? Math.min(100, Math.round((storageData.gb / STORAGE_LIMIT_GB) * 100))
    : 0;

  // Storage bar color based on usage
  const getStorageColor = (percent) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Get storage percent badge color
  const getStoragePercentBadge = (percent) => {
    if (percent >= 90) return 'bg-red-100 text-red-700';
    if (percent >= 70) return 'bg-amber-100 text-amber-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Inne metryki</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Storage Usage Card */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-slate-500" />
                Wykorzystanie przestrzeni
              </CardTitle>
              {!storageLoading && (
                <span className={cn(
                  "px-2 py-0.5 text-xs font-medium rounded-full",
                  getStoragePercentBadge(storagePercent)
                )}>
                  {storagePercent}%
                </span>
              )}
            </div>
            <CardDescription>
              Zdjęcia i załączniki
            </CardDescription>
          </CardHeader>
          <CardContent>
            {storageLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-2 w-full" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Main Value */}
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-slate-900">
                    {storageData?.gb > 1 ? storageData.gb : storageData?.mb || 0}
                  </span>
                  <span className="text-sm text-slate-500">
                    {storageData?.gb > 1 ? 'GB' : 'MB'} / {STORAGE_LIMIT_GB} GB
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="relative">
                  <Progress
                    value={storagePercent}
                    className="h-2"
                  />
                  <div
                    className={cn(
                      "absolute inset-0 h-2 rounded-full transition-all",
                      getStorageColor(storagePercent)
                    )}
                    style={{ width: `${storagePercent}%` }}
                  />
                </div>

                {/* Photo Count */}
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Camera className="w-4 h-4" />
                  <span>{storageData?.photoCount || 0} zdjęć</span>
                </div>

                {/* Warning if near limit */}
                {storagePercent >= 90 && (
                  <p className="text-xs text-red-600 font-medium">
                    Przestrzeń prawie pełna! Rozważ usunięcie starych zdjęć.
                  </p>
                )}
                {storagePercent >= 70 && storagePercent < 90 && (
                  <p className="text-xs text-amber-600">
                    Wykorzystano ponad 70% przestrzeni.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Users Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              Aktywni rodzice
            </CardTitle>
            <CardDescription>
              Ostatnie 7 dni
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeUsersLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Main Value */}
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-slate-900">
                    {activeUsersData?.activeCount || 0}
                  </span>
                  <span className="text-sm text-slate-500">
                    z {activeUsersData?.totalParents || 0} rodziców
                  </span>
                </div>

                {/* Percentage */}
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
                    activeUsersData?.percentage >= 70
                      ? "bg-emerald-100 text-emerald-700"
                      : activeUsersData?.percentage >= 40
                        ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700"
                  )}>
                    <TrendingUp className="w-3 h-3" />
                    {activeUsersData?.percentage || 0}% aktywność
                  </div>
                </div>

                {/* Visual Representation */}
                <div className="grid grid-cols-10 gap-1 mt-2">
                  {Array.from({ length: 10 }).map((_, i) => {
                    const threshold = ((i + 1) / 10) * 100;
                    const isActive = (activeUsersData?.percentage || 0) >= threshold;
                    return (
                      <div
                        key={i}
                        className={cn(
                          "h-6 rounded-sm transition-colors",
                          isActive ? "bg-emerald-500" : "bg-slate-200"
                        )}
                      />
                    );
                  })}
                </div>

                {/* Engagement Tip */}
                {activeUsersData?.percentage < 40 && activeUsersData?.totalParents > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    Spróbuj dodać więcej zdjęć lub ogłoszeń, aby zwiększyć zaangażowanie rodziców.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parents Without Photo Consent Card */}
        <Card className="md:col-span-2 lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CameraOff className="w-4 h-4 text-amber-500" />
              Brak zgody na zdjęcia
            </CardTitle>
            <CardDescription>
              Rodzice bez zgody na udostępnianie zdjęć
            </CardDescription>
          </CardHeader>
          <CardContent>
            {photoConsentLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center gap-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-amber-600">
                      {photoConsentData?.parentsWithoutConsent?.length || 0}
                    </span>
                    <span className="text-sm text-slate-500">
                      z {photoConsentData?.totalParents || 0} rodziców
                    </span>
                  </div>
                  {photoConsentData?.parentsWithoutConsent?.length > 0 && (
                    <div className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                      Brak zgody
                    </div>
                  )}
                </div>

                {/* Expandable list */}
                {photoConsentData?.parentsWithoutConsent?.length > 0 && (
                  <Collapsible open={noConsentExpanded} onOpenChange={setNoConsentExpanded}>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform",
                        noConsentExpanded && "rotate-180"
                      )} />
                      {noConsentExpanded ? 'Ukryj listę' : 'Pokaż listę rodziców'}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                        {photoConsentData.parentsWithoutConsent.map((parent) => (
                          <div
                            key={parent.id}
                            className="p-2 bg-slate-50 rounded-lg"
                          >
                            <p className="text-sm font-medium text-slate-900">
                              {parent.first_name} {parent.last_name}
                            </p>
                            <p className="text-xs text-slate-500">{parent.email}</p>
                            {/* Children */}
                            <div className="flex flex-wrap gap-1 mt-1">
                              {parent.children.map((child) => (
                                <span
                                  key={child.id}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-200 text-slate-700 text-xs rounded-full"
                                >
                                  <Baby className="w-3 h-3" />
                                  {child.first_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* All good message */}
                {photoConsentData?.parentsWithoutConsent?.length === 0 && photoConsentData?.totalParents > 0 && (
                  <p className="text-sm text-emerald-600 font-medium">
                    ✓ Wszyscy rodzice wyrazili zgodę
                  </p>
                )}

                {/* No parents yet */}
                {photoConsentData?.totalParents === 0 && (
                  <p className="text-sm text-slate-500">
                    Brak rodziców w oddziale
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Teacher-Child Ratio Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-slate-500" />
              Współczynnik nauczyciel — dzieci
            </CardTitle>
            <CardDescription>
              Liczba dzieci przypadająca na nauczyciela
            </CardDescription>
          </CardHeader>
          <CardContent>
            {ratioLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Main Value */}
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-slate-900">
                    {ratioData?.ratioDisplay || '-'}
                  </span>
                </div>

                {/* Breakdown */}
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4" />
                    <span>{ratioData?.teacherCount || 0} nauczycieli</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Baby className="w-4 h-4" />
                    <span>{ratioData?.childrenCount || 0} dzieci</span>
                  </div>
                </div>

                {/* No teachers message */}
                {ratioData?.teacherCount === 0 && (
                  <p className="text-sm text-slate-500">
                    Brak przypisanych nauczycieli
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
